import { type Question } from 'shared-domains';
import ObjectId from 'bson-objectid';

import Constants from '~/config/Constants';
import QuestionModel from '~/models/Question';
import Structure from '~/models/Structure';
import QuestionOption from '~/models/QuestionOption';
import { OrderType } from 'shared-domains';
import QuestionTarget from '../models/QuestionTarget';
import i18n from '~/services/i18n';
import FeatureFlagsService from '~/services/FeatureFlagsService';
import QuestionFactoryTemplate from './QuestionFactoryTemplate';

const QuestionFactory = {
  createQuestion(type: string, checkTemplateCreation: boolean = false, qId: string = ObjectId().toHexString()): Question {
    const questionId = qId;

    if (checkTemplateCreation && FeatureFlagsService().getFeatureValue(Constants.FeatureFlagsTypes.SHOW_EDITOR_QUESTION_TEMPLATES)) {
      const question = QuestionFactoryTemplate.createQuestionTemplate(type, questionId);
      if (question) {
        return question;
      }
    }

    switch (type) {
      case Constants.SlideTypes.MCQ:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.MCQ,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.MCQ],
          structure: Structure({
            kind: Constants.SlideTypes.MCQ,
            options: [...Array(4)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.MSQ:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.MSQ,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.MSQ],
          structure: Structure({
            answer: [],
            kind: Constants.SlideTypes.MSQ,
            options: [...Array(4)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.BLANK:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.BLANK,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.BLANK],
          structure: Structure({
            answer: 0,
            hints: [],
            options: [QuestionOption()],
            kind: Constants.SlideTypes.BLANK,
            settings: {
              hasCorrectAnswer: true,
              fibDataType: Constants.TypedQuestionResponseTypes.STRING,
              enableAccentMarks: false,
              questionMetadata: {
                answerLength: 0,
                specialIndices: [],
              },
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.OPEN:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.OPEN,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.OPEN],
          structure: Structure({
            answer: [],
            kind: Constants.SlideTypes.OPEN,
            settings: {
              hasCorrectAnswer: false,
              fibDataType: Constants.TypedQuestionResponseTypes.STRING,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.POLL:
        /**
         * `POLL` Question type is a frontend only thing. Internally it is either an MCQ or MSQ question
         * with just the setting `hasCorrectAnswer` equal to false.
         */
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.MCQ,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.MCQ],
          structure: Structure({
            kind: Constants.SlideTypes.MCQ,
            options: [...Array(4)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: false,
            },
            marks: {
              correct: 0,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.DRAW:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.DRAW,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.DRAW],
          structure: Structure({
            kind: Constants.SlideTypes.DRAW,
            settings: {
              hasCorrectAnswer: false,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.AUDIO:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.AUDIO,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.AUDIO],
          structure: Structure({
            kind: Constants.SlideTypes.AUDIO,
            settings: {
              hasCorrectAnswer: false,
            },
            query: { answerTime: 30000 },
            answer: [-1],
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.VIDEO:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.VIDEO,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.VIDEO],
          structure: Structure({
            kind: Constants.SlideTypes.VIDEO,
            settings: {
              hasCorrectAnswer: false,
            },
            query: { answerTime: 30000 },
            answer: [-1],
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.REORDER:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.REORDER,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.REORDER],
          structure: Structure({
            query: {
              text: `<p>${i18n('Reorder the following')}</p>`,
            },
            kind: Constants.SlideTypes.REORDER,
            options: [...Array(5)].map(() => QuestionOption()),
            order: OrderType.ASCENDING,
            answer: [],
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.MATCH:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.MATCH,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.MATCH],
          structure: Structure({
            query: {
              text: `<p>${i18n('Match the following')}</p>`,
            },
            kind: Constants.SlideTypes.MATCH,
            answer: [],
            options: [...Array(5)].map(() => QuestionOption()),
            matches: [...Array(5)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.DROPDOWN:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.DROPDOWN,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.DROPDOWN],
          structure: Structure({
            kind: Constants.SlideTypes.DROPDOWN,
            answer: [],
            settings: {
              hasCorrectAnswer: true,
              doesOptionHaveMultipleTargets: false,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.DRAGNDROP:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.DRAGNDROP,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.DRAGNDROP],
          structure: Structure({
            kind: Constants.SlideTypes.DRAGNDROP,
            answer: [],
            settings: {
              hasCorrectAnswer: true,
              doesOptionHaveMultipleTargets: false,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.EQUATION:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.EQUATION,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.EQUATION],
          structure: Structure({
            kind: Constants.SlideTypes.EQUATION,
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.DND_IMAGE:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.DND_IMAGE,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.DND_IMAGE],
          structure: Structure({
            kind: Constants.SlideTypes.DND_IMAGE,
            answer: [],
            targets: [],
            options: [...Array(4)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.HOTSPOT:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.HOTSPOT,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.HOTSPOT],
          structure: Structure({
            kind: Constants.SlideTypes.HOTSPOT,
            answer: [],
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.GRAPHING:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.GRAPHING,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.GRAPHING],
          structure: Structure({
            kind: Constants.SlideTypes.GRAPHING,
            answer: [],
            graphs: [],
            settings: {
              hasCorrectAnswer: true,
              doesOptionHaveMultipleTargets: false,
              graphData: {
                sizer: 1,
                type: 'Linear',
                maxPoints: 2,
              },
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
      case Constants.SlideTypes.WORDCLOUD:
        /**
         * `WORDCLOUD` Question type is a frontend only thing. Internally it is a OPEN ENDED question
         * with just the setting `hasCorrectAnswer` equal to false.
         */
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.WORDCLOUD,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.WORDCLOUD],
          structure: Structure({
            kind: Constants.SlideTypes.WORDCLOUD,
            settings: {
              hasCorrectAnswer: false,
              fibDataType: Constants.TypedQuestionResponseTypes.STRING,
              questionDisplayVariant: Constants.DisplayVariantNames[Constants.QuestionTypes.WORDCLOUD],
            },
            marks: {
              correct: 0,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.CLASSIFICATION:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.CLASSIFICATION,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.CLASSIFICATION],
          structure: Structure({
            query: {
              text: '<p><i18n>Organize these options into the right categories</i18n></p>',
            },
            kind: Constants.SlideTypes.CLASSIFICATION,
            answer: [],
            targets: [...Array(4)].map(() => QuestionTarget()),
            settings: {
              hasCorrectAnswer: true,
              doesOptionHaveMultipleTargets: false,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.COMPREHENSION:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.COMPREHENSION,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.COMPREHENSION],
          comprehensionQuestionIds: [],
          structure: Structure({
            kind: Constants.SlideTypes.COMPREHENSION,
            answer: [],
            settings: {
              hasCorrectAnswer: false,
            },
            marks: {
              correct: 0,
              incorrect: 0,
            },
          }),
        });

      case Constants.SlideTypes.COMPREHENSION_VIDEO:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.COMPREHENSION_VIDEO,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.COMPREHENSION_VIDEO],
          comprehensionQuestionIds: [],
          structure: Structure({
            kind: Constants.SlideTypes.COMPREHENSION_VIDEO,
            answer: [],
            settings: {
              hasCorrectAnswer: false,
            },
            marks: {
              correct: 0,
              incorrect: 0,
            },
          }),
        });

      default:
        return QuestionModel({
          _id: questionId,
          type: Constants.SlideTypes.MCQ,
          time: Constants.QuestionDefaultTimes[Constants.SlideTypes.MCQ],
          structure: Structure({
            kind: Constants.SlideTypes.MCQ,
            options: [...Array(4)].map(() => QuestionOption()),
            settings: {
              hasCorrectAnswer: true,
            },
            marks: {
              correct: 1,
              incorrect: 0,
            },
          }),
        });
    }
  },
};

export default QuestionFactory;
